import { useEffect, useRef, useState } from 'react';

interface AddCompetitorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (url: string) => void | Promise<void>;
  loading?: boolean;
}

export function AddCompetitorModal({
  isOpen,
  onClose,
  onConfirm,
  loading = false,
}: AddCompetitorModalProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [url, setUrl] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setUrl('');
      setIsVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
          setTimeout(() => {
            inputRef.current?.focus();
          }, 50);
        });
      });
    } else if (isVisible) {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isVisible]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !loading) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isVisible, loading, onClose]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!url.trim()) return;
    await onConfirm(url.trim());
  };

  if (!isVisible) return null;

  return (
    <div
      className={`rename-modal ${isVisible ? 'is-visible' : ''} ${isAnimating ? 'is-open' : ''} ${
        !isAnimating && isVisible ? 'is-closing' : ''
      }`}
    >
      <div className="white-background">
        <div
          className="modal-close"
          onClick={loading ? undefined : onClose}
          style={{ cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.5 : 1 }}
        >
          <div className="rotated-text">+</div>
        </div>

        <div className="text-size-medium text-weight-bold">Add Competitor</div>
        <div className="spacer-xsmall"></div>

        <form onSubmit={handleSubmit} className="add-folder-form-block w-form">
          <label htmlFor="competitor-url" className="form_label">
            Competitor URL
          </label>
          <input
            ref={inputRef}
            id="competitor-url"
            type="text"
            className="form_input w-input"
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            placeholder="e.g. competitor.com"
            maxLength={256}
            required
            disabled={loading}
          />
          <div className="spacer-small"></div>
          <button
            type="submit"
            className={`button w-button${url.trim() ? '' : ' is-lighter'}`}
            disabled={loading || !url.trim()}
            style={{ opacity: loading ? 0.6 : 1, width: '100%' }}
          >
            {loading ? 'Please wait...' : 'Add Competitor'}
          </button>
        </form>
      </div>

      <div
        className="close-layer"
        onClick={loading ? undefined : onClose}
        style={{ cursor: loading ? 'not-allowed' : 'pointer' }}
      ></div>
    </div>
  );
}
